import { apiClient } from "./client";

export type TeacherAssistantMode = "standalone" | "chat";
export type TeacherAssistantAction =
  | "learning_plan"
  | "program_match"
  | "clarify_request"
  | "draft_reply";

export interface LearningPlanTopic { module: string; topic: string }
export interface LearningPlanStage {
  title: string;
  topics: LearningPlanTopic[];
  practice: string;
  outcome: string;
}
export interface LearningPlan {
  title: string;
  goal: string;
  starting_level: string;
  stages: LearningPlanStage[];
  final_result: string;
  assumptions: string[];
}

export interface TeacherAssistantSession {
  session_id: string;
  expires_in: number;
  mode: TeacherAssistantMode;
  history?: TeacherAssistantHistoryItem[];
}

export interface TeacherAssistantHistoryItem {
  role: "assistant" | "user";
  content: string;
  message_id?: string | null;
  response_type?: "recommendations" | "answer" | "learning_plan" | null;
  recommendations?: TeacherRecommendation[];
  learning_plan?: LearningPlan | null;
  feedback_rating?: "up" | "down" | null;
}

export interface TeacherAssistantReply {
  message_id: string;
  response_type: "recommendations" | "answer" | "learning_plan";
  answer: string;
  result?: {
    recommendations?: TeacherRecommendation[];
    learning_plan?: LearningPlan | null;
  };
}

export interface TeacherRecommendation {
  teacher: string;
  score: number;
  reason: string;
}

export const teacherAssistantService = {
  status: async (): Promise<{ enabled: boolean; curricula_ready: boolean }> =>
    (await apiClient.get<{ enabled: boolean; curricula_ready: boolean }>("/teacher-assistant/status")).data,
  create: async (
    mode: TeacherAssistantMode,
    idempotencyKey: string,
    chatId?: string,
  ): Promise<TeacherAssistantSession> => {
    const path =
      mode === "chat" && chatId
        ? `/chats/${encodeURIComponent(chatId)}/teacher-assistant/sessions`
        : "/teacher-assistant/sessions";
    return (
      await apiClient.post<TeacherAssistantSession>(path, undefined, {
        headers: { "X-Idempotency-Key": idempotencyKey },
      })
    ).data;
  },
  send: async (
    sessionId: string,
    message: string,
    chatId?: string,
    action?: TeacherAssistantAction,
  ): Promise<TeacherAssistantReply> =>
    (
      await apiClient.post<TeacherAssistantReply>(
        `/teacher-assistant/sessions/${encodeURIComponent(sessionId)}/messages`,
        { message, chat_id: chatId || null, ...(action ? { action } : {}) },
        { timeout: 120000 },
      )
    ).data,
  remove: async (sessionId: string, chatId?: string): Promise<void> => {
    await apiClient.delete(
      `/teacher-assistant/sessions/${encodeURIComponent(sessionId)}`,
      { params: chatId ? { chat_id: chatId } : undefined },
    );
  },
  feedback: async (
    sessionId: string,
    messageId: string,
    rating: "up" | "down",
    comment: string,
    chatId?: string,
  ): Promise<{ ok: boolean; created: boolean }> =>
    (
      await apiClient.post<{ ok: boolean; created: boolean }>(
        `/teacher-assistant/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/feedback`,
        { rating, comment, chat_id: chatId || null },
      )
    ).data,
};
